// React page for browsing/cleaning harvest records and reviewing yield-by-age
// stats — both feed predictive modelling (view/delete keeps the source data
// clean; yield-by-age turns it into "how much can I expect at day N").
import { useCallback, useEffect, useMemo, useState } from 'react';
import AiTag from '../components/AiTag';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { api } from '../lib/auth';
import { Button, Input, Select, PageHeader } from '../lib/ui';

// Categorical slots 1 (blue) & 2 (orange) — fixed order, validated CVD-safe
// (adjacent ΔE 24.7 protan / 33.6 normal, ≥3:1 on white) against this page's
// panel surface via the dataviz skill's validator.
const FULL_COLOR    = '#2a78d6';
const PARTIAL_COLOR = '#eb6834';

const th = { textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: 'var(--muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.04em' };
const td = { padding: '10px 12px', verticalAlign: 'middle', fontSize: 13, color: 'var(--text)' };

const LIMIT = 100;

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Records tab ──────────────────────────────────────────────────────────────
function RecordsTab() {
  const [records, setRecords] = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(1);
  const [err, setErr]         = useState('');

  const [cropType, setCropType] = useState('');
  const [from, setFrom]         = useState('');
  const [to, setTo]             = useState('');

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    setErr('');
    try {
      const params = new URLSearchParams({ limit: LIMIT, skip: (pg - 1) * LIMIT });
      if (cropType) params.set('cropType', cropType);
      if (from)     params.set('from', from);
      if (to)       params.set('to', to);

      const r = await api.get(`/bench-layout/harvested?${params}`);
      setRecords(r.data.records || []);
      setTotal(r.data.total || 0);
      setPage(pg);
    } catch {
      setErr('Failed to load harvest records');
    } finally {
      setLoading(false);
    }
  }, [cropType, from, to]);

  useEffect(() => { load(1); }, [load]);

  const del = async (rec) => {
    if (!window.confirm(`Delete this ${rec.cropType || 'harvest'} record (${rec.weight}g, ${formatDate(rec.harvestedAt)})? This can't be undone.`)) return;
    try {
      await api.delete(`/bench-layout/harvested/${rec._id}`);
      setRecords(rs => rs.filter(r => r._id !== rec._id));
      setTotal(t => t - 1);
    } catch {
      setErr('Failed to delete record');
    }
  };

  const cropOptions = [...new Set(records.map(r => r.cropType).filter(Boolean))].sort();
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      {err && <div style={{ padding: 10, background: '#fff5f5', border: '1px solid #f5c6cb', marginBottom: 12, borderRadius: 'var(--r-lg)', fontSize: 13, color: '#b00020' }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <Select value={cropType} onChange={e => setCropType(e.target.value)} style={{ width: 180 }}>
          <option value="">All crops</option>
          {cropOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </Select>
        <Input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ width: 150 }} />
        <Input type="date" value={to}   onChange={e => setTo(e.target.value)}   style={{ width: 150 }} />
        {(cropType || from || to) && (
          <Button onClick={() => { setCropType(''); setFrom(''); setTo(''); }} style={{ fontSize: 12 }}>
            Clear filters
          </Button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted)', alignSelf: 'center' }}>{total} record{total === 1 ? '' : 's'}</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg)' }}>
              <th style={th}>Harvested</th>
              <th style={th}>Crop</th>
              <th style={th}>Batch</th>
              <th style={th}>Location</th>
              <th style={th}>Stage</th>
              <th style={{ ...th, textAlign: 'right' }}>Weight (g)</th>
              <th style={th}>Type</th>
              <th style={{ ...th, textAlign: 'right' }}>% taken</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>Loading…</td></tr>
            )}
            {!loading && records.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No harvest records found.</td></tr>
            )}
            {!loading && records.map(r => (
              <tr key={r._id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={td}>{formatDate(r.harvestedAt)}</td>
                <td style={td}>{r.cropType || '—'}<AiTag record={r} /></td>
                <td style={td}>{r.batchId || '—'}</td>
                <td style={td}>{[r.bench, r.layer != null ? `L${r.layer}` : null, r.tray != null ? `T${r.tray}` : null].filter(Boolean).join(' · ') || '—'}</td>
                <td style={td}>{r.stage || '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>{r.weight}</td>
                <td style={td}>{r.partial ? 'Partial' : 'Full'}</td>
                <td style={{ ...td, textAlign: 'right', color: r.partial && r.percentTaken == null ? 'var(--muted)' : 'var(--text)' }}>
                  {r.partial ? (r.percentTaken != null ? `${r.percentTaken}%` : 'unrecorded') : '—'}
                </td>
                <td style={td}>
                  <Button variant="danger" onClick={() => del(r)} style={{ fontSize: 12, padding: '5px 10px' }}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', marginTop: 12 }}>
          <Button onClick={() => load(page - 1)} disabled={page <= 1} style={{ padding: '6px 10px', fontSize: 12 }}>← Prev</Button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Page {page} of {totalPages} ({total} total)</span>
          <Button onClick={() => load(page + 1)} disabled={page >= totalPages} style={{ padding: '6px 10px', fontSize: 12 }}>Next →</Button>
        </div>
      )}
    </>
  );
}

// ── Yield-by-age tab ─────────────────────────────────────────────────────────
// Full and partial harvests are kept in separate tables: a partial (a snip
// off a tray that keeps growing) and a full (the whole tray cleared) aren't
// comparable weights at the same age, so they'd mislead if averaged together.
function ByAgeTable({ byAge, barColor, showPercent }) {
  const maxAvg = Math.max(...byAge.map(a => a.avgWeight), 1);

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}>Age (days)</th>
          <th style={{ ...th, textAlign: 'right' }}>Avg weight (g)</th>
          <th style={{ ...th, textAlign: 'right' }}>Min–Max (g)</th>
          {showPercent && <th style={{ ...th, textAlign: 'right' }}>Avg % of tray taken</th>}
          <th style={{ ...th, textAlign: 'right' }}>Samples</th>
          <th style={th}>Relative</th>
        </tr>
      </thead>
      <tbody>
        {byAge.map(a => (
          <tr key={a.ageDays} style={{ borderBottom: '1px solid var(--border)' }}>
            <td style={td}>{a.ageDays}</td>
            <td style={{ ...td, textAlign: 'right' }}>{a.avgWeight}</td>
            <td style={{ ...td, textAlign: 'right', color: 'var(--muted)' }}>{a.minWeight}–{a.maxWeight}</td>
            {showPercent && (
              <td style={{ ...td, textAlign: 'right', color: a.avgPercentTaken == null ? 'var(--muted)' : 'var(--text)' }}>
                {a.avgPercentTaken != null ? `${a.avgPercentTaken}% (n=${a.percentSampleCount})` : 'unrecorded'}
              </td>
            )}
            <td style={{ ...td, textAlign: 'right' }}>{a.count}</td>
            <td style={td}>
              <div style={{ background: 'var(--panel-2)', borderRadius: 'var(--r-sm)', height: 8, width: '100%', maxWidth: 200 }}>
                <div style={{ background: barColor, borderRadius: 'var(--r-sm)', height: 8, width: `${(a.avgWeight / maxAvg) * 100}%` }} />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Fills the gaps between real (sampled) ages with a straight-line estimate,
// only *within* the observed range — never extrapolated past the earliest or
// latest actual harvest, since there's no evidence to project from out there.
function buildInterpolatedSeries(byAge) {
  if (!byAge || byAge.length === 0) return [];

  const minDay = byAge[0].ageDays;
  const maxDay = byAge[byAge.length - 1].ageDays;
  const byDay = new Map(byAge.map(a => [a.ageDays, a]));

  const points = [];
  let lo = 0;

  for (let day = minDay; day <= maxDay; day++) {
    const exact = byDay.get(day);
    if (exact) {
      points.push({ day, value: exact.avgWeight, real: true, sample: exact });
      continue;
    }
    while (lo < byAge.length - 2 && byAge[lo + 1].ageDays < day) lo++;
    const a = byAge[lo], b = byAge[lo + 1];
    const t = (day - a.ageDays) / (b.ageDays - a.ageDays);
    const value = Math.round((a.avgWeight + t * (b.avgWeight - a.avgWeight)) * 10) / 10;
    points.push({ day, value, real: false, sample: null });
  }

  return points;
}

// Full and partial run on independent day ranges, so this is an outer join
// on day - each row only has whichever series actually covers that day.
function mergeSeries(fullPoints, partialPoints) {
  const byDay = new Map();

  for (const p of fullPoints) {
    byDay.set(p.day, { day: p.day, full: p.value, fullReal: p.real, fullSample: p.sample, partial: null, partialReal: false, partialSample: null });
  }
  for (const p of partialPoints) {
    const row = byDay.get(p.day) || { day: p.day, full: null, fullReal: false, fullSample: null };
    row.partial = p.value;
    row.partialReal = p.real;
    row.partialSample = p.sample;
    byDay.set(p.day, row);
  }

  return [...byDay.values()].sort((a, b) => a.day - b.day);
}

// Only draws a marker on days with an actual measured sample - an
// interpolated day is line-only, so measured vs. estimated is never
// ambiguous at a glance.
function RealDot({ cx, cy, payload, dataKey }) {
  const realKey = dataKey === 'full' ? 'fullReal' : 'partialReal';
  if (!payload?.[realKey] || cx == null || cy == null) return null;
  const color = dataKey === 'full' ? FULL_COLOR : PARTIAL_COLOR;
  return <circle cx={cx} cy={cy} r={4} fill={color} stroke="var(--panel)" strokeWidth={2} />;
}

function YieldTooltip({ active, payload, label }) {
  if (!active || !payload) return null;
  const rows = payload.filter(p => p.value != null);
  if (rows.length === 0) return null;

  return (
    <div style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: '8px 10px', fontSize: 12, boxShadow: 'var(--shadow-card)' }}>
      <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Day {label}</div>
      {rows.map(p => {
        const isFull = p.dataKey === 'full';
        const isReal = p.payload[isFull ? 'fullReal' : 'partialReal'];
        const sample = p.payload[isFull ? 'fullSample' : 'partialSample'];
        const percentNote = !isFull && isReal
          ? (sample.avgPercentTaken != null ? `, ~${sample.avgPercentTaken}% of tray` : ', % of tray unrecorded')
          : '';
        return (
          <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <span style={{ display: 'inline-block', width: 10, height: 2, background: p.color, flexShrink: 0 }} />
            <span style={{ color: 'var(--muted)' }}>{isFull ? 'Full' : 'Partial'}:</span>
            <strong style={{ color: 'var(--text)' }}>{p.value}g</strong>
            <span style={{ color: 'var(--muted)', fontSize: 11 }}>
              {isReal ? `(measured, n=${sample.count}, ${sample.minWeight}–${sample.maxWeight}g${percentNote})` : '(estimated)'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function CropYieldChart({ crop }) {
  const data = useMemo(() => {
    const fullPoints    = buildInterpolatedSeries(crop.full);
    const partialPoints = buildInterpolatedSeries(crop.partial);
    return mergeSeries(fullPoints, partialPoints);
  }, [crop]);

  if (data.length === 0) {
    return <div style={{ color: 'var(--muted)', padding: 24, textAlign: 'center' }}>Not enough data yet to chart this crop.</div>;
  }

  return (
    <div style={{ width: '100%', height: 300 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 4, bottom: 20 }}>
          <CartesianGrid stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="day" type="number" domain={['dataMin', 'dataMax']}
            tick={{ fontSize: 11, fill: 'var(--muted)' }} stroke="var(--border)"
            label={{ value: 'Age (days since seeding)', position: 'insideBottom', offset: -8, fontSize: 11, fill: 'var(--muted)' }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'var(--muted)' }} stroke="var(--border)"
            label={{ value: 'Weight (g)', angle: -90, position: 'insideLeft', fontSize: 11, fill: 'var(--muted)' }}
          />
          <Tooltip content={<YieldTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={(value) => <span style={{ color: 'var(--text)' }}>{value}</span>} />
          <Line type="linear" dataKey="full"    name="Full harvest"    stroke={FULL_COLOR}    strokeWidth={2} dot={<RealDot />} connectNulls={false} isAnimationActive={false} />
          <Line type="linear" dataKey="partial" name="Partial harvest" stroke={PARTIAL_COLOR} strokeWidth={2} dot={<RealDot />} connectNulls={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function YieldByAgeTab() {
  const [crops, setCrops] = useState([]);
  const [excluded, setExcluded] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [selectedCrop, setSelectedCrop] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await api.get('/bench-layout/harvested/yield-by-age');
      const loadedCrops = r.data.crops || [];
      setCrops(loadedCrops);
      setExcluded(r.data.excludedRecords || 0);
      setTotal(r.data.totalRecords || 0);
      setSelectedCrop(prev => (prev && loadedCrops.some(c => c.cropType === prev)) ? prev : (loadedCrops[0]?.cropType || ''));
    } catch {
      setErr('Failed to load yield-by-age data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const crop = crops.find(c => c.cropType === selectedCrop);

  return (
    <>
      {err && <div style={{ padding: 10, background: '#fff5f5', border: '1px solid #f5c6cb', marginBottom: 12, borderRadius: 'var(--r-lg)', fontSize: 13, color: '#b00020' }}>{err}</div>}

      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
        Age is days from seeding to harvest, recovered from each record's seed date (falling back to the batch ID when it's missing).
        {total > 0 && ` ${excluded} of ${total} record(s) had no usable seed date and were left out.`}
        {' '}Dots mark actual measured harvests; the line between them is a straight-line estimate, never projected past the earliest or latest real sample.
      </div>

      {loading && <div style={{ color: 'var(--muted)', padding: 24, textAlign: 'center' }}>Loading…</div>}
      {!loading && crops.length === 0 && <div style={{ color: 'var(--muted)', padding: 24, textAlign: 'center' }}>Not enough data yet.</div>}

      {!loading && crops.length > 0 && (
        <>
          <div style={{ marginBottom: 14 }}>
            <Select value={selectedCrop} onChange={e => setSelectedCrop(e.target.value)} style={{ width: 220 }}>
              {crops.map(c => <option key={c.cropType} value={c.cropType}>{c.cropType}</option>)}
            </Select>
          </div>

          {crop && (
            <div style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: '14px 16px', marginBottom: 16 }}>
              <CropYieldChart crop={crop} />
            </div>
          )}

          {crop && (
            <div style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 6 }}>
                Full harvests {crop.full.length === 0 && <span style={{ fontWeight: 400, textTransform: 'none' }}>— none yet</span>}
              </div>
              {crop.full.length > 0 && (
                <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                  <ByAgeTable byAge={crop.full} barColor={FULL_COLOR} />
                </div>
              )}

              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 6 }}>
                Partial harvests {crop.partial.length === 0 && <span style={{ fontWeight: 400, textTransform: 'none' }}>— none yet</span>}
              </div>
              {crop.partial.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <ByAgeTable byAge={crop.partial} barColor={PARTIAL_COLOR} showPercent />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function HarvestsPage() {
  const [tab, setTab] = useState('records');

  return (
    <div className="dashboard-container">
      <PageHeader
        title="Harvests"
        tabs={[{ key: 'records', label: 'Records' }, { key: 'yieldByAge', label: 'Yield by age' }]}
        activeTab={tab}
        onTabChange={setTab}
      />

      {tab === 'records' ? <RecordsTab /> : <YieldByAgeTab />}
    </div>
  );
}
